import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

Turbo.StreamActions.community_replies_changed = function () {
  this.targetElements.forEach(element => element.dispatchEvent(new Event("community:replies-changed")))
}

export default class extends Controller {
  static values = { url: String, publicationId: String }

  connect() {
    this.stopped = false
    this.generation = (this.generation || 0) + 1
    this.running = false
    this.submissions = new Set()
    this.listeners = []
    this.listen(this.element, "community:replies-changed", () => this.schedule())
    this.listen(document, "visibilitychange", () => this.schedule())
    this.listen(document, "turbo:frame-load", event => {
      if (event.target.id === `conversation_${this.publicationIdValue}`) this.schedule()
    })
    this.listen(document, "click", event => {
      if (event.target.closest(`[aria-controls="discussion_${this.publicationIdValue}"]`)) this.schedule()
    })
    this.listen(document, "turbo:submit-start", event => this.submission(event, true))
    this.listen(document, "turbo:submit-end", event => this.submission(event, false))
    const source = this.element.querySelector("turbo-cable-stream-source")
    this.sourceObserver = new MutationObserver(() => {
      if (source.hasAttribute("connected")) this.schedule()
    })
    if (source) this.sourceObserver.observe(source, { attributes: true, attributeFilter: ["connected"] })
    this.schedule()
  }

  disconnect() {
    this.stopped = true
    this.generation++
    clearTimeout(this.timer)
    this.abortController?.abort()
    this.sourceObserver?.disconnect()
    this.listeners.forEach(([element, name, handler]) => element.removeEventListener(name, handler))
  }

  listen(element, name, handler) {
    element.addEventListener(name, handler)
    this.listeners.push([element, name, handler])
  }

  get publication() { return document.getElementById(this.publicationIdValue) }
  get list() { return document.getElementById(`${this.publicationIdValue}--comments`) }
  get expanded() {
    const discussion = document.getElementById(`discussion_${this.publicationIdValue}`)
    return !!this.list && (!discussion || !discussion.classList.contains("d-none"))
  }

  submission(event, started) {
    const form = event.target
    if (!this.publication?.contains(form) && !document.getElementById(`conversation_${this.publicationIdValue}`)?.contains(form)) return
    this.generation++
    if (started) {
      this.submissions.add(form)
      this.abortController?.abort()
    } else {
      this.submissions.delete(form)
      this.schedule()
    }
  }

  schedule() {
    if (this.stopped) return
    this.pending = true
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.synchronize(), 80)
  }

  async synchronize() {
    if (this.stopped || this.running || document.hidden || this.submissions.size) return
    this.running = true
    this.pending = false
    const generation = this.generation
    const abortController = new AbortController()
    this.abortController = abortController
    try {
      const url = new URL(this.urlValue, window.location.href)
      if (this.expanded) url.searchParams.set("expanded", "true")
      const response = await fetch(url.toString(), {
        headers: { Accept: "text/vnd.turbo-stream.html" },
        credentials: "same-origin", signal: abortController.signal
      })
      if (this.stopped || generation !== this.generation) return
      if ([401, 403, 404].includes(response.status) || response.redirected ||
          (response.ok && !response.headers.get("Content-Type")?.includes("text/vnd.turbo-stream.html"))) {
        this.removeConversation()
      } else if (response.ok) {
        const html = await response.text()
        if (!this.stopped && generation === this.generation) await this.renderResponse(html, generation)
      }
    } catch {
      // Keep any newer signal queued; reconnect and visibility changes also retry failures.
    } finally {
      if (this.abortController === abortController) {
        this.running = false
        if (this.pending && !this.stopped) this.schedule()
      }
    }
  }

  async renderResponse(html, generation) {
    const template = document.createElement("template")
    template.innerHTML = html
    template.content.querySelectorAll("turbo-stream").forEach(stream => {
      stream.setAttribute("data-live-replies", this.element.id)
    })
    const renders = []
    const beforeRender = event => {
      if (event.target.getAttribute("data-live-replies") !== this.element.id) return
      const original = event.detail.render
      let resolve
      renders.push(new Promise(done => { resolve = done }))
      event.detail.render = async stream => {
        try {
          if (this.stopped || generation !== this.generation) return
          if (stream.getAttribute("target") === `${this.publicationIdValue}--comments` && !this.expanded) return
          const scroll = [window.scrollX, window.scrollY]
          const preserve = event => {
            if (!this.list?.contains(event.target)) return
            if (event.detail.newElement && event.target.matches('form[data-controller~="comment-form"], [data-comment-target="replyForm"]')) event.preventDefault()
          }
          const preserveAttribute = event => {
            if (this.list?.contains(event.target) && event.detail.attributeName === "aria-expanded" && event.target.matches('[data-action="comment#toggleReply"]')) event.preventDefault()
          }
          document.addEventListener("turbo:before-morph-element", preserve)
          document.addEventListener("turbo:before-morph-attribute", preserveAttribute)
          try { await original(stream) } finally {
            document.removeEventListener("turbo:before-morph-element", preserve)
            document.removeEventListener("turbo:before-morph-attribute", preserveAttribute)
            window.scrollTo(...scroll)
          }
        } finally { resolve() }
      }
    }
    document.addEventListener("turbo:before-stream-render", beforeRender)
    try {
      Turbo.renderStreamMessage(template.innerHTML)
      await Promise.all(renders)
    } finally {
      document.removeEventListener("turbo:before-stream-render", beforeRender)
    }
  }

  removeConversation() {
    this.disconnect()
    document.getElementById(`conversation_${this.publicationIdValue}`)?.remove()
    this.publication?.remove()
    this.list?.remove()
    this.element.remove()
  }
}
